import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Search, Filter, CheckCircle, XCircle, Save, ArrowLeft,
  UserPlus, UserMinus, ChevronDown, ChevronUp, Trophy, Info
} from 'lucide-react';
import { eventGymnastService, eventService, levelService } from '../../services/supabaseService';
import { LEVEL_OPTIONS, FRANJA_OPTIONS } from '../../utils/constants';
import { calculateAge } from '../../utils/formatters';
import './ClubEventAthleteManager.css';

const ClubEventAthleteManager = ({ event, clubId, athletes, athleteImages, onBack, notify }) => {
  const [eventDetail, setEventDetail] = useState(event);
  const [eventLevels, setEventLevels] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterFranja, setFilterFranja] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'enrolled' | 'not-enrolled'
  const [showFilters, setShowFilters] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [initialEnrolledIds, setInitialEnrolledIds] = useState(new Set());

  // Cargar datos del evento y atletas inscritas
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar detalle del evento (para obtener niveles permitidos)
      const { data: evData } = await eventService.getById(event.id);
      if (evData) setEventDetail(evData);

      // Cargar niveles del evento
      const { data: levels } = await levelService.getEventLevels(event.id);
      setEventLevels(levels || []);

      // Cargar atletas ya inscritas
      const { data: enrolled } = await eventGymnastService.getByEventAndClub(event.id, clubId);
      const ids = new Set((enrolled || []).map(e => e.gymnast_id));
      setEnrolledIds(ids);
      setInitialEnrolledIds(new Set(ids));
    } catch (err) {
      console.error('Error cargando datos:', err);
      notify?.error?.('Error al cargar datos del evento');
    } finally {
      setLoading(false);
    }
  }, [event.id, clubId, notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Detectar cambios pendientes
  useEffect(() => {
    const hasChanges = enrolledIds.size !== initialEnrolledIds.size ||
      [...enrolledIds].some(id => !initialEnrolledIds.has(id)) ||
      [...initialEnrolledIds].some(id => !enrolledIds.has(id));
    setPendingChanges(hasChanges);
  }, [enrolledIds, initialEnrolledIds]);

  // Niveles permitidos del evento (nombres)
  const allowedLevelNames = useMemo(() => {
    if (!eventLevels || eventLevels.length === 0) return [];
    return eventLevels.map(l => l.name).filter(Boolean);
  }, [eventLevels]);

  // Filtrar atletas
  const filteredAthletes = useMemo(() => {
    let list = [...(athletes || [])];

    // Buscar por nombre
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a => (a.name || '').toLowerCase().includes(q));
    }

    // Filtrar por nivel
    if (filterLevel !== 'all') {
      list = list.filter(a => a.level === filterLevel);
    }

    // Filtrar por franja
    if (filterFranja !== 'all') {
      list = list.filter(a => a.franja === filterFranja);
    }

    // Filtrar por estado de inscripción
    if (filterStatus === 'enrolled') {
      list = list.filter(a => enrolledIds.has(a.id));
    } else if (filterStatus === 'not-enrolled') {
      list = list.filter(a => !enrolledIds.has(a.id));
    }

    return list;
  }, [athletes, search, filterLevel, filterFranja, filterStatus, enrolledIds]);

  // Toggle atleta
  const toggleAthlete = (athleteId) => {
    setEnrolledIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(athleteId)) {
        newSet.delete(athleteId);
      } else {
        newSet.add(athleteId);
      }
      return newSet;
    });
  };

  // Seleccionar todas las filtradas
  const selectAllFiltered = () => {
    setEnrolledIds(prev => {
      const newSet = new Set(prev);
      filteredAthletes.forEach(a => newSet.add(a.id));
      return newSet;
    });
  };

  // Deseleccionar todas las filtradas
  const deselectAllFiltered = () => {
    setEnrolledIds(prev => {
      const newSet = new Set(prev);
      filteredAthletes.forEach(a => newSet.delete(a.id));
      return newSet;
    });
  };

  // Guardar selección
  const handleSave = async () => {
    setSaving(true);
    try {
      const gymnastIds = [...enrolledIds];
      const { error } = await eventGymnastService.syncEventGymnasts(event.id, gymnastIds, clubId);
      if (error) throw new Error(error);
      setInitialEnrolledIds(new Set(enrolledIds));
      setPendingChanges(false);
      notify?.success?.(`✅ ${gymnastIds.length} atleta${gymnastIds.length !== 1 ? 's' : ''} inscrita${gymnastIds.length !== 1 ? 's' : ''} en el evento`);
    } catch (err) {
      notify?.error?.('Error al guardar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const enrolledCount = enrolledIds.size;
  const totalAthletes = athletes?.length || 0;
  const filteredCount = filteredAthletes.length;
  const allFilteredSelected = filteredAthletes.length > 0 && filteredAthletes.every(a => enrolledIds.has(a.id));

  const getInitials = (name) => name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '?';

  if (loading) {
    return (
      <div className="ceam-loading">
        <div className="ceam-spinner"></div>
        <p>Cargando información del evento...</p>
      </div>
    );
  }

  return (
    <div className="ceam-container">
      {/* Header */}
      <div className="ceam-header">
        <button className="ceam-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Volver
        </button>
        <div className="ceam-header-info">
          <h2 className="ceam-title">
            <Trophy size={20} /> Administrar Atletas
          </h2>
          <p className="ceam-subtitle">{eventDetail?.name || event.name}</p>
          <p className="ceam-date">
            {eventDetail?.date ? new Date(eventDetail.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
            {eventDetail?.location ? ` • ${eventDetail.location}` : ''}
          </p>
        </div>
      </div>

      {/* Niveles del evento */}
      {allowedLevelNames.length > 0 && (
        <div className="ceam-info-banner">
          <Info size={16} />
          <div>
            <strong>Niveles del evento:</strong>
            <div className="ceam-level-tags">
              {allowedLevelNames.map(lvl => (
                <span key={lvl} className="ceam-level-tag">{lvl}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="ceam-stats">
        <div className="ceam-stat">
          <Users size={16} />
          <span><strong>{enrolledCount}</strong> inscritas</span>
        </div>
        <div className="ceam-stat">
          <span>de <strong>{totalAthletes}</strong> atletas</span>
        </div>
        {pendingChanges && (
          <div className="ceam-stat ceam-stat-warning">
            <span>⚠️ Cambios sin guardar</span>
          </div>
        )}
      </div>

      {/* Toolbar */}
      <div className="ceam-toolbar">
        <div className="ceam-search">
          <Search size={16} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar atleta..."
          />
        </div>
        <button
          className={`ceam-filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          Filtros
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="ceam-filters">
          <div className="ceam-filter-group">
            <label>Nivel</label>
            <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
              <option value="all">Todos los niveles</option>
              {allowedLevelNames.length > 0 && (
                <optgroup label="Niveles del evento">
                  {allowedLevelNames.map(lvl => (
                    <option key={`event-${lvl}`} value={lvl}>{lvl}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Todos los niveles">
                {LEVEL_OPTIONS.map(lvl => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className="ceam-filter-group">
            <label>Franja</label>
            <select value={filterFranja} onChange={(e) => setFilterFranja(e.target.value)}>
              <option value="all">Todas las franjas</option>
              {FRANJA_OPTIONS.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="ceam-filter-group">
            <label>Estado</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Todas</option>
              <option value="enrolled">Inscritas</option>
              <option value="not-enrolled">No inscritas</option>
            </select>
          </div>
          {allowedLevelNames.length > 0 && (
            <button
              className="ceam-quick-filter-btn"
              onClick={() => setFilterLevel(allowedLevelNames[0])}
              title="Filtrar solo por niveles del evento"
            >
              🎯 Solo niveles del evento
            </button>
          )}
        </div>
      )}

      {/* Bulk actions */}
      <div className="ceam-bulk-actions">
        <span className="ceam-filtered-count">
          Mostrando {filteredCount} de {totalAthletes} atletas
        </span>
        <div className="ceam-bulk-btns">
          <button
            className="ceam-bulk-btn ceam-bulk-select"
            onClick={selectAllFiltered}
            disabled={allFilteredSelected || filteredCount === 0}
          >
            <UserPlus size={14} /> Seleccionar todas
          </button>
          <button
            className="ceam-bulk-btn ceam-bulk-deselect"
            onClick={deselectAllFiltered}
            disabled={filteredCount === 0}
          >
            <UserMinus size={14} /> Deseleccionar todas
          </button>
        </div>
      </div>

      {/* Athletes List */}
      <div className="ceam-list">
        {filteredAthletes.length === 0 ? (
          <div className="ceam-empty">
            <Users size={40} />
            <p>No se encontraron atletas con los filtros actuales</p>
          </div>
        ) : (
          filteredAthletes.map(athlete => {
            const isEnrolled = enrolledIds.has(athlete.id);
            const matchesEventLevel = allowedLevelNames.length === 0 || allowedLevelNames.includes(athlete.level);
            return (
              <div
                key={athlete.id}
                className={`ceam-athlete-row ${isEnrolled ? 'enrolled' : ''} ${!matchesEventLevel ? 'mismatch' : ''}`}
                onClick={() => toggleAthlete(athlete.id)}
              >
                <div className="ceam-athlete-check">
                  {isEnrolled ? (
                    <CheckCircle size={22} className="ceam-check-on" />
                  ) : (
                    <div className="ceam-check-off" />
                  )}
                </div>
                <div className="ceam-athlete-avatar">
                  {athleteImages?.[athlete.id] ? (
                    <img src={athleteImages[athlete.id]} alt={athlete.name} />
                  ) : (
                    <span>{getInitials(athlete.name)}</span>
                  )}
                </div>
                <div className="ceam-athlete-info">
                  <h4>{athlete.name}</h4>
                  <div className="ceam-athlete-meta">
                    {athlete.level && (
                      <span className={`ceam-meta-tag ${matchesEventLevel ? 'ceam-tag-match' : 'ceam-tag-mismatch'}`}>
                        {athlete.level}
                      </span>
                    )}
                    {athlete.franja && <span className="ceam-meta-tag ceam-tag-franja">{athlete.franja}</span>}
                    {(athlete.birth_date || athlete.age) && <span className="ceam-meta-tag">{calculateAge(athlete.birth_date) ?? athlete.age} años</span>}
                  </div>
                </div>
                {!matchesEventLevel && (
                  <span className="ceam-mismatch-badge" title="El nivel de esta atleta no coincide con los niveles del evento">
                    ⚠️
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Floating Save Button */}
      {pendingChanges && (
        <div className="ceam-save-bar">
          <div className="ceam-save-info">
            <span>{enrolledCount} atleta{enrolledCount !== 1 ? 's' : ''} seleccionada{enrolledCount !== 1 ? 's' : ''}</span>
          </div>
          <button
            className="ceam-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar Selección'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ClubEventAthleteManager;
